import { Button, Select, Text, TextField, SidePanel } from '@nike/eds'
import { Column, ColumnFiltersState } from '@tanstack/react-table'

export type SidePanelColumn<T = any> = Column<T, any> & {
  columnDef: {
    header: string | React.ReactNode | (() => React.ReactNode)
    filterLabel?: string
    filterType?: 'text' | 'select' | 'dateRange' | 'date'
    filterOptions?: Array<{ value: string; label: string }>
    getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
  }
}

type ColumnFiltersSidePanelProps = {
    isOpen: boolean
    onClose: () => void
    table: any
    columns: {
        id: string
        getCanFilter: () => boolean
        getFilterValue: () => string | object | undefined
        setFilterValue: (value: string | object | undefined) => void
        columnDef: {
            header: string | React.ReactNode | (() => React.ReactNode);
            filterLabel?: string;
            filterType?: 'text' | 'select' | 'dateRange' | 'date'
            filterOptions?: Array<{ value: string; label: string }>
            getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>
            filterKey?: string;
        }
    }[]
    columnFilters: ColumnFiltersState
    setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
    handleColumnFiltersChange?: (filters: ColumnFiltersState) => void
}

export function ColumnFiltersSidePanel(
    {
        isOpen,
        onClose,
        table,
        columns,
        columnFilters,
        handleColumnFiltersChange,
    }: ColumnFiltersSidePanelProps) {

    const handleClearFilters = () => {
        if (handleColumnFiltersChange) {
            handleColumnFiltersChange([])
        }
    }
    
    const handleFilterChange = (columnId: string, value: string | object | undefined) => {
        // Build new filters array based on all columns' current filter values
        const newFilters = columns
            .map(col => {
                const val = col.id === columnId ? value : col.getFilterValue();
                return val !== undefined && val !== '' ? { id: col.id, value: val } : null;
            })
            .filter(Boolean) as ColumnFiltersState;

        if (handleColumnFiltersChange) {
            handleColumnFiltersChange(newFilters);
        }
    };
  return (
    <SidePanel
        hideScrim={true}
        isOpen={isOpen}
        onDismiss={onClose}
        headerSlot={<><Text font="title-5">Filters</Text></>}
        footerSlot={
           <Button disabled={columnFilters.length === 0} style={{ width: "100%" }} variant="primary" onClick={handleClearFilters}>Clear Filters</Button>
        }
        hideFade={true}>
            
      <div className="eds-spacing--pb--64" style={{'minHeight': '50vh'}}>
        {columns.map((column) => {
            if (!column.getCanFilter()) return null
            const filterValue = column.getFilterValue() as string | { from?: string; to?: string }
            const filterType = column.columnDef.filterType || 'text'
            const label =
                column.columnDef.filterLabel ||
                (typeof column.columnDef.header === 'string' ? column.columnDef.header : '');

            let filterOptions =
                column.columnDef.getFilterOptions?.(table) ||
                column.columnDef.filterOptions ||
                [];
                // Ensure "All" is the first option
            if (
                filterType === 'select' &&
                (!filterOptions.length || filterOptions[0].value !== '')
            ) {
                filterOptions = [{ value: '', label: 'All' }, ...filterOptions];
            }
            return (
                <div key={column.id} className="eds-flex eds-flex--direction-column eds-gap--8 eds-spacing--mb-16">
                    {filterType === 'text' && (
                        <TextField 
                            label={label}
                            id={`table-sidepanel-filter-${column.id}`}
                            type="search"
                            value={typeof filterValue === 'string' ? filterValue : ''}
                            onChange={(e) => handleFilterChange(column.id, e.target.value || undefined)}
                            placeholder="Type to filter..."/>)}
                    {filterType === 'date' && (
                        <TextField
                            label={label}
                            id={`table-sidepanel-filter-${column.id}`}
                            type="date"
                            value={typeof filterValue === 'string' ? filterValue : ''}
                            onChange={(e) => handleFilterChange(column.id, e.target.value || undefined)}
                        />
                    )}
                    {filterType === 'select' && (
                        <Select
                            id={`table-sidepanel-filter-select-${column.id}`}
                            label={label}
                            menuPosition='absolute'
                            message=''
                            options={filterOptions}
                            value={
                                filterOptions.find((option) => option.value === filterValue) || {
                                    value: '',
                                    label: 'All',
                                }
                            }
                            onChange={(e) => handleFilterChange(column.id, e?.value || undefined)}
                        />)}
                    {filterType === 'dateRange' && (
                        <>
                        <Text font={'title-5'}>{label}</Text>
                        <div className="eds-flex eds-gap--8">
                            <TextField
                                id={`table-sidepanel-filter-date-from-${column.id}`}
                                label="From"
                                type="date"
                                value={typeof filterValue === 'object' && filterValue?.from ? filterValue.from : ''}
                                onChange={(e) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), from: e.target.value || undefined })}
                            />
                            <TextField
                                id={`table-sidepanel-filter-date-to-${column.id}`}
                                label="To"
                                type="date"
                                value={typeof filterValue === 'object' && filterValue?.to ? filterValue.to : ''}
                                onChange={(e) => handleFilterChange(column.id, { ...(typeof filterValue === 'object' && filterValue ? filterValue : {}), to: e.target.value || undefined })}
                            />
                        </div>
                        </>
                    )}
                </div>)
            
            })}
        </div>
    </SidePanel>
  )
}
